import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Users, LayoutDashboard, FileText, Sparkles, Printer, Languages, Repeat, KeyRound, ClipboardList, BarChart3 } from "lucide-react";
import { useState } from "react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { VanIcon } from "@/components/VanIcon";
import { useI18n } from "@/context/I18nContext";
import { LANGS } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, changeLang, canTranslate, editMode, setEditMode } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [pwOpen, setPwOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const linkCls = (path) =>
    `px-1.5 xl:px-2 min-[1600px]:px-3 py-1.5 min-[1600px]:py-2 text-sm font-medium rounded-md transition-colors ${
      loc.pathname === path
        ? "bg-primary/15 text-primary"
        : "text-muted-foreground hover:text-foreground hover:bg-accent"
    }`;

  if (!user) return null;

  const links = [
    { to: "/", id: "configurator", icon: Sparkles, label: t("Configurateur") },
    { to: "/devis", id: "quotes", icon: FileText, label: t("Mes devis") },
    { to: "/clients", id: "clients", icon: Users, label: t("Clients") },
    ...(user.role === "admin" || user.adv ? [{ to: "/adv", id: "adv", icon: ClipboardList, label: t("Commandes à traiter") }] : []),
    { to: "/tarifs", id: "prices", icon: Printer, label: t("Tarifs") },
    { to: "/expos", id: "expos", icon: VanIcon, label: t("Véhicules à la vente") },
    { to: "/reprises", id: "tradeins", icon: Repeat, label: t("Mes reprises") },
    ...(user.role === "admin" || user.sales_manager ? [{ to: "/statistiques", id: "stats", icon: BarChart3, label: t("Statistiques salons") }] : []),
    ...(user.role === "admin" || user.can_translate ? [{ to: "/traductions", id: "translations", icon: Languages, label: t("Traductions") }] : []),
    ...(user.role === "admin" ? [{ to: "/admin", id: "admin", icon: LayoutDashboard, label: "Admin" }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
            <img src="/assets/fautras-logo.png" alt="Fautras" className="h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.filter((l) => l.id !== "admin").map(({ to, id, icon: Icon, label }) => (
              <Link key={id} to={to} data-testid={`nav-${id === "configurator" ? "configurator" : id}`} className={linkCls(to)}>
                <span className="inline-flex flex-col min-[1600px]:flex-row items-center gap-0.5 min-[1600px]:gap-1.5" title={label}><Icon className="w-4 h-4 shrink-0" /><span className="text-[10px] leading-tight text-center max-w-[64px] min-[1600px]:max-w-none min-[1600px]:text-sm min-[1600px]:whitespace-nowrap" data-testid={`nav-label-${id}`}>{label}</span></span>
              </Link>
            ))}
            {user.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className={linkCls("/admin")}>
                <span className="inline-flex flex-col min-[1600px]:flex-row items-center gap-0.5 min-[1600px]:gap-1.5 whitespace-nowrap" title="Admin"><LayoutDashboard className="w-4 h-4 shrink-0" /><span className="text-[10px] leading-tight min-[1600px]:text-sm" data-testid="nav-label-admin">Admin</span></span>
              </Link>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2 ml-3">
        {canTranslate && lang !== "fr" && (
          <Button variant={editMode ? "default" : "outline"} size="sm" className="gap-1.5 h-9" onClick={() => setEditMode(!editMode)} title={t("Corriger traduction")} data-testid="translate-mode-toggle" data-translate-ui>
            <Languages className="w-4 h-4" /> <span>{t("Corriger traduction")}</span>
          </Button>
        )}
        <Select value={lang} onValueChange={changeLang}>
          <SelectTrigger className="w-[78px] h-9 text-xs" data-testid="lang-select"><Languages className="w-3.5 h-3.5 mr-1" /><span>{lang.toUpperCase()}</span></SelectTrigger>
          <SelectContent>{LANGS.map(([c, n]) => <SelectItem key={c} value={c} data-testid={`lang-${c}`}>{c.toUpperCase()} · {n}</SelectItem>)}</SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="gap-2">
              <User className="w-4 h-4" />
              <span className="hidden lg:inline max-w-[140px] truncate">{user.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user.name}</div>
              <div className="text-xs text-muted-foreground font-normal truncate">{user.email}</div>
              <div className="text-[10px] uppercase tracking-wide text-primary font-bold mt-1">
                {user.role === "admin" ? "Administrateur" : "Revendeur"}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/devis")} data-testid="menu-quotes">
              <FileText className="w-4 h-4 mr-2" /> {t("Mes devis")}
            </DropdownMenuItem>
            {user.role === "admin" && (
              <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                <LayoutDashboard className="w-4 h-4 mr-2" /> Administration
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password">
              <KeyRound className="w-4 h-4 mr-2" /> {t("Changer mon mot de passe")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> {t("Déconnexion")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
        </div>
      </div>
      <nav className="md:hidden flex flex-wrap items-center gap-1 px-3 pb-2 -mt-1" data-testid="mobile-nav">
        {links.map(({ to, id, icon: Icon, label }) => (
          <Link key={id} to={to} data-testid={`mobile-nav-${id}`} className={`${linkCls(to)} shrink-0 whitespace-nowrap`}>
            <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</span>
          </Link>
        ))}
      </nav>
    </header>
  );
}
